/**
 * Guided "Create a marketplace listing" flow (web) — Phase 5, seller side.
 *
 * A four-step wizard that an INDEPENDENT (non-coop) farmer uses to publish one of
 * their LOCKED harvests to the marketplace. It is the richer alternative to the
 * inline publish form on the harvest-detail page (_app.my-harvests.$id.tsx) — that
 * page now just links here with `?harvestId=<id>` pre-selected.
 *
 *   Step 1 — Choose source: the caller's LOCKED, owned harvests (GET /harvests
 *            ?status=locked). Gated to non-coop farmers (a coop member's harvest is
 *            coop-managed and the server 403s a publish regardless — we hide it).
 *            If a valid `?harvestId=` is supplied we pre-select it and skip to step 2.
 *   Step 2 — Price & details: ask price (R/kg, positive), optional grade (A/B/C),
 *            optional note (≤500). Same money handling as the inline section.
 *   Step 3 — Photo (OPTIONAL): show the harvest's existing cover photo or let the
 *            farmer upload one via uploadHarvestPhoto (so the listing has a
 *            buyer-visible cover, TD-135). Publishing does NOT require a photo.
 *   Step 4 — Review & publish: summary + publishListing({ sourceKind:'harvest', … }).
 *            On success → /marketplace/:id (the new listing's buyer-facing detail).
 *
 * MONEY: askPriceZarpPerKgMicro is a micro-ZARP (1e-6 ZARP) integer carried as a
 * STRING on the wire — we convert the R/kg decimal exactly as the inline section
 * does (round to nearest micro-unit) and never Number() it for math.
 *
 * Search param: this tree is assembled by hand in router.ts (which this lane does
 * NOT edit), so the route isn't given a `validateSearch`. We read `?harvestId=`
 * straight off `window.location.search` at mount and UUID-guard it; a missing /
 * malformed value just starts the flow at step 1.
 *
 * Online-only (no offline outbox on web): a transport / guard failure surfaces as
 * an inline error. Export: `CreateListingScreen` (central wires it at
 * path '/create-listing' under the _app auth gate — see report).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Money,
  Select,
  Stack,
  StatusPill,
  Stepper,
  Text,
  TextField,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readAccessTokenClaims } from '../api/client';
import { harvestsControllerList } from '../api/generated/harvests/harvests';
import { meControllerGetFarmProfile } from '../api/generated/me/me';
import type { HarvestDto } from '../api/generated/models';
import {
  fetchHarvestPhotoObjectUrl,
  listHarvestPhotos,
  uploadHarvestPhoto,
} from '../api/photos';
// IMPORTANT: imported from the Lane-F-owned marketplace client; do NOT edit that file.
import { microToZarp, publishListing } from '../api/marketplace';
import { groupDigits } from '../lib/format';

const MICRO_PER_ZAR = 1_000_000;
const NOTE_MAX = 500;
/** Client-side upload guard — mirrors the server's image-only ≤5 MB limit. */
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const GRADES = ['A', 'B', 'C'] as const;
type Grade = (typeof GRADES)[number];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** "12.50" R/kg → micro-ZARP integer STRING, or null if not a positive number. */
function zarPerKgToMicroString(input: string): string | null {
  const n = Number(input);
  if (!Number.isFinite(n) || n <= 0) return null;
  return String(Math.round(n * MICRO_PER_ZAR));
}

/** Read + UUID-guard `?harvestId=` from the URL (route has no validateSearch here). */
function readHarvestIdParam(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('harvestId');
  return raw && UUID_RE.test(raw) ? raw : null;
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

export function CreateListingScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sub = readAccessTokenClaims()?.sub;

  // `?harvestId=` is read once at mount (the URL doesn't change as we step).
  const [preselectId] = useState<string | null>(readHarvestIdParam);

  // Wizard state.
  const [step, setStep] = useState(0); // 0..3
  const [selectedId, setSelectedId] = useState<string | null>(preselectId);
  const [price, setPrice] = useState('');
  const [grade, setGrade] = useState<Grade | ''>('');
  const [note, setNote] = useState('');

  // Read-only when in a coop. Fail OPEN: on error/404 treat as NOT in a coop
  // (mirrors the harvest-detail gate). A coop member can't publish — the server
  // 403s — so we hide the whole flow rather than show a broken affordance.
  const farmProfileQuery = useQuery({
    queryKey: ['me-farm-profile'],
    queryFn: ({ signal }) => meControllerGetFarmProfile(signal),
    enabled: !!sub,
    retry: false,
  });
  const isInCoop = farmProfileQuery.data?.cooperativeId != null;

  // Step 1 source list: the caller's LOCKED, owned harvests (owner-scoped server
  // side). Only locked harvests are listable — open still takes weights,
  // sold/settled is done. We don't try to detect "already listed" client-side
  // (no per-harvest listing flag on the DTO); the server rejects a duplicate
  // publish and we surface that error on step 4.
  const harvestsQuery = useQuery({
    queryKey: ['create-listing', 'locked-harvests'],
    queryFn: ({ signal }) =>
      harvestsControllerList({ status: 'locked', limit: 100 }, signal),
    enabled: !!sub && !isInCoop,
  });
  const lockedHarvests = useMemo<HarvestDto[]>(
    () => harvestsQuery.data?.data ?? [],
    [harvestsQuery.data],
  );

  const selectedHarvest = useMemo(
    () => lockedHarvests.find((h) => h.id === selectedId) ?? null,
    [lockedHarvests, selectedId],
  );

  // If a valid `?harvestId=` was supplied AND it's a real locked harvest, jump
  // straight to step 2 (price). Runs once the list resolves. If the id isn't in
  // the list (not locked / not owned) we drop the preselection and stay on step 1.
  const jumpedRef = useRef(false);
  useEffect(() => {
    if (jumpedRef.current || !preselectId || !harvestsQuery.isSuccess) return;
    jumpedRef.current = true;
    const match = lockedHarvests.find((h) => h.id === preselectId);
    if (match) {
      setSelectedId(match.id);
      setStep(1);
    } else {
      setSelectedId(null);
    }
  }, [preselectId, harvestsQuery.isSuccess, lockedHarvests]);

  const micro = zarPerKgToMicroString(price);
  const priceValid = micro != null;

  const publish = useMutation({
    mutationFn: () =>
      publishListing({
        sourceKind: 'harvest',
        sourceId: selectedId as string,
        askPriceZarpPerKgMicro: micro as string,
        grade: grade || undefined,
        note: note.trim() ? note.trim() : undefined,
      }),
    onSuccess: (listing) => {
      // Refresh the seller's listings view + the harvest detail (publish state)
      // so a back-nav shows the new listing.
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
      if (selectedId) {
        queryClient.invalidateQueries({ queryKey: ['my-harvests', selectedId] });
      }
      navigate({ to: '/marketplace/$id', params: { id: listing.id } });
    },
  });

  const steps = [
    t('createListing.steps.source', 'Source'),
    t('createListing.steps.details', 'Price'),
    t('createListing.steps.photo', 'Photo'),
    t('createListing.steps.review', 'Review'),
  ];

  // --- Coop gate: hide the whole flow for a coop member (server 403s a publish).
  // We wait for the farm-profile to resolve so we don't flash the flow then hide it.
  const farmProfileReady = !farmProfileQuery.isPending;

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Text variant="headline-medium" as="h2">
          {t('createListing.title', 'Create a listing')}
        </Text>
        <Link to="/my-listings" style={{ textDecoration: 'none' }}>
          <Button variant="text">{t('createListing.myListings', 'My listings')}</Button>
        </Link>
      </Box>

      {!farmProfileReady ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isInCoop ? (
        <Alert severity="info">
          {t(
            'createListing.coopBlocked',
            'Coop harvests are managed by your cooperative, so you cannot publish them yourself.',
          )}
        </Alert>
      ) : (
        <>
          <Stepper steps={steps} activeStep={step} />

          {step === 0 ? (
            <SourceStep
              harvests={lockedHarvests}
              isLoading={harvestsQuery.isPending}
              isError={harvestsQuery.isError}
              error={harvestsQuery.error}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onRetry={() => harvestsQuery.refetch()}
              onNext={() => setStep(1)}
              t={t}
            />
          ) : null}

          {step === 1 ? (
            <DetailsStep
              harvest={selectedHarvest}
              price={price}
              setPrice={setPrice}
              grade={grade}
              setGrade={setGrade}
              note={note}
              setNote={setNote}
              priceValid={priceValid}
              onBack={() => setStep(0)}
              onNext={() => setStep(2)}
              t={t}
            />
          ) : null}

          {step === 2 && selectedId ? (
            <PhotoStep
              harvestId={selectedId}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
              t={t}
            />
          ) : null}

          {step === 3 && selectedHarvest ? (
            <ReviewStep
              harvest={selectedHarvest}
              priceMicro={micro}
              grade={grade}
              note={note}
              onBack={() => setStep(2)}
              onPublish={() => publish.mutate()}
              isPublishing={publish.isPending}
              publishError={publish.isError ? publish.error : null}
              t={t}
            />
          ) : null}
        </>
      )}
    </Stack>
  );
}

// --------------------------------------------------------------------------
// Step 1 — Choose source
// --------------------------------------------------------------------------

interface SourceStepProps {
  harvests: HarvestDto[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onRetry: () => void;
  onNext: () => void;
  t: TFunction;
}

function SourceStep({
  harvests,
  isLoading,
  isError,
  error,
  selectedId,
  onSelect,
  onRetry,
  onNext,
  t,
}: SourceStepProps) {
  return (
    <Stack spacing={2}>
      <Box>
        <Text variant="title-large" as="h3">
          {t('createListing.source.title', 'Choose a harvest')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t(
              'createListing.source.subtitle',
              'Only locked harvests can be listed. Lock a harvest first if it is not here yet.',
            )}
          </Text>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              {t('createListing.retry', 'Retry')}
            </Button>
          }
        >
          {t('createListing.source.loadError', {
            defaultValue: 'Could not load your harvests: {{error}}',
            error: error instanceof FseApiError ? error.message : String(error),
          })}
        </Alert>
      ) : harvests.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Text variant="title-medium" as="h4">
                {t('createListing.source.empty.title', 'No locked harvests')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t(
                  'createListing.source.empty.body',
                  'You have no locked harvests to list. Open a harvest, record its weights, then lock it.',
                )}
              </Text>
              <Box sx={{ mt: 1 }}>
                <Link to="/my-harvests" style={{ textDecoration: 'none' }}>
                  <Button variant="outlined">
                    {t('createListing.source.empty.action', 'Go to my harvests')}
                  </Button>
                </Link>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {harvests.map((h) => {
            const selected = h.id === selectedId;
            return (
              <Card
                key={h.id}
                variant="outlined"
                onClick={() => onSelect(h.id)}
                sx={{
                  cursor: 'pointer',
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderWidth: selected ? 2 : 1,
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Text variant="title-medium" as="span">
                          <Box component="span" sx={{ fontFamily: 'monospace' }}>
                            {t('createListing.source.harvestRef', {
                              defaultValue: 'Harvest {{id}}',
                              id: shortId(h.id),
                            })}
                          </Box>
                        </Text>
                        <StatusPill variant="upcoming">
                          {t('createListing.source.lockedBadge', 'Locked')}
                        </StatusPill>
                      </Stack>
                      <Text variant="body-medium" color="text.secondary">
                        {t('createListing.source.totalKg', {
                          defaultValue: '{{qty}} kg total',
                          qty: groupDigits(h.totalKg),
                        })}
                        {h.crop ? ` · ${h.crop}` : ''}
                      </Text>
                    </Stack>
                    {selected ? (
                      <Box component="span" sx={{ color: 'primary.main', display: 'inline-flex' }}>
                        <Icon name="check_circle" />
                      </Box>
                    ) : (
                      <Box component="span" sx={{ color: 'text.disabled', display: 'inline-flex' }}>
                        <Icon name="radio_button_unchecked" />
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" disabled={!selectedId} onClick={onNext}>
          {t('createListing.next', 'Next')}
        </Button>
      </Box>
    </Stack>
  );
}

// --------------------------------------------------------------------------
// Step 2 — Price & details
// --------------------------------------------------------------------------

interface DetailsStepProps {
  harvest: HarvestDto | null;
  price: string;
  setPrice: (v: string) => void;
  grade: Grade | '';
  setGrade: (v: Grade | '') => void;
  note: string;
  setNote: (v: string) => void;
  priceValid: boolean;
  onBack: () => void;
  onNext: () => void;
  t: TFunction;
}

function DetailsStep({
  harvest,
  price,
  setPrice,
  grade,
  setGrade,
  note,
  setNote,
  priceValid,
  onBack,
  onNext,
  t,
}: DetailsStepProps) {
  return (
    <Stack spacing={2}>
      <Box>
        <Text variant="title-large" as="h3">
          {t('createListing.details.title', 'Price & details')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t(
              'createListing.details.subtitle',
              'Set your asking price per kilogram. Verified buyers make offers; you accept or reject each one.',
            )}
          </Text>
        </Box>
      </Box>

      {harvest ? (
        <Card variant="outlined">
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('createListing.details.forHarvest', {
                defaultValue: 'Listing {{qty}} kg from harvest {{id}}',
                qty: groupDigits(harvest.totalKg),
                id: shortId(harvest.id),
              })}
            </Text>
          </CardContent>
        </Card>
      ) : null}

      <TextField
        label={t('createListing.details.priceLabel', 'Asking price (R / kg)')}
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        inputMode="decimal"
        error={price.length > 0 && !priceValid}
        helperText={
          price.length > 0 && !priceValid
            ? t('createListing.details.priceError', 'Enter a positive amount')
            : ' '
        }
        fullWidth
      />

      <FormControl fullWidth>
        <InputLabel id="create-listing-grade-label">
          {t('createListing.details.gradeLabel', 'Grade (optional)')}
        </InputLabel>
        <Select
          labelId="create-listing-grade-label"
          label={t('createListing.details.gradeLabel', 'Grade (optional)')}
          value={grade}
          onChange={(e) => setGrade(e.target.value as Grade | '')}
        >
          <MenuItem value="">
            {t('createListing.details.gradeNone', 'No grade')}
          </MenuItem>
          {GRADES.map((g) => (
            <MenuItem key={g} value={g}>
              {t('createListing.details.gradeOption', {
                defaultValue: 'Grade {{grade}}',
                grade: g,
              })}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label={t('createListing.details.noteLabel', 'Note for buyers (optional)')}
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
        multiline
        minRows={2}
        slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
        helperText={t('createListing.details.noteCount', {
          defaultValue: '{{count}}/{{max}}',
          count: note.length,
          max: NOTE_MAX,
        })}
        fullWidth
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="text" onClick={onBack}>
          {t('createListing.back', 'Back')}
        </Button>
        <Button variant="contained" disabled={!priceValid} onClick={onNext}>
          {t('createListing.next', 'Next')}
        </Button>
      </Box>
    </Stack>
  );
}

// --------------------------------------------------------------------------
// Step 3 — Photo (optional)
// --------------------------------------------------------------------------

interface PhotoStepProps {
  harvestId: string;
  onBack: () => void;
  onNext: () => void;
  t: TFunction;
}

/**
 * Shows the harvest's existing cover photo (newest, from listHarvestPhotos) and,
 * when there is none, lets the farmer upload one so the listing has a
 * buyer-visible cover (TD-135). A photo is OPTIONAL — the farmer can always
 * continue without one and the publish does not require it.
 */
function PhotoStep({ harvestId, onBack, onNext, t }: PhotoStepProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const photosQuery = useQuery({
    queryKey: ['harvest-photos', harvestId],
    queryFn: () => listHarvestPhotos(harvestId),
    enabled: harvestId.length > 0,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadHarvestPhoto(harvestId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['harvest-photos', harvestId] });
    },
    onSettled: () => {
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    setValidationError(null);
    uploadMutation.reset();
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setValidationError(t('createListing.photo.invalidType', 'Choose an image file.'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setValidationError(t('createListing.photo.tooLarge', 'Image must be 5 MB or smaller.'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    uploadMutation.mutate(file);
  }

  // Newest photo first (the API returns newest-first) → the listing cover.
  const cover = photosQuery.data?.[0] ?? null;

  return (
    <Stack spacing={2}>
      <Box>
        <Text variant="title-large" as="h3">
          {t('createListing.photo.title', 'Cover photo')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t(
              'createListing.photo.subtitle',
              'A photo helps buyers — but it is optional. You can publish without one.',
            )}
          </Text>
        </Box>
      </Box>

      {photosQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : cover ? (
        <CoverPreview harvestId={harvestId} photoId={cover.id} t={t} />
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1} sx={{ alignItems: 'center', py: 2 }}>
              <Box component="span" sx={{ color: 'text.disabled', display: 'inline-flex' }}>
                <Icon name="inventory_2" size={32} />
              </Box>
              <Text variant="body-medium" color="text.secondary">
                {t('createListing.photo.empty', 'No photo yet.')}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Box>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onPickFile}
          style={{ display: 'none' }}
        />
        <Button
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMutation.isPending}
          startIcon={
            uploadMutation.isPending ? <CircularProgress size={16} /> : <Icon name="add" />
          }
        >
          {uploadMutation.isPending
            ? t('createListing.photo.uploading', 'Uploading…')
            : cover
              ? t('createListing.photo.replace', 'Replace photo')
              : t('createListing.photo.add', 'Add a photo')}
        </Button>

        {validationError ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {validationError}
          </Alert>
        ) : null}
        {uploadMutation.isError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('createListing.photo.uploadError', {
              defaultValue: 'Could not upload the photo: {{error}}',
              error:
                uploadMutation.error instanceof Error
                  ? uploadMutation.error.message
                  : String(uploadMutation.error),
            })}
          </Alert>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="text" onClick={onBack}>
          {t('createListing.back', 'Back')}
        </Button>
        <Button variant="contained" onClick={onNext} disabled={uploadMutation.isPending}>
          {t('createListing.next', 'Next')}
        </Button>
      </Box>
    </Stack>
  );
}

interface CoverPreviewProps {
  harvestId: string;
  photoId: string;
  t: TFunction;
}

/**
 * Render one harvest photo as the cover preview. The bytes sit behind auth, so we
 * fetch → blob → object URL and revoke it on unmount (mirrors the harvest-detail
 * PhotoThumb).
 */
function CoverPreview({ harvestId, photoId, t }: CoverPreviewProps) {
  const urlQuery = useQuery({
    queryKey: ['harvest-photo-url', photoId],
    queryFn: () => fetchHarvestPhotoObjectUrl(harvestId, photoId),
  });
  const objectUrl = urlQuery.data;

  useEffect(() => {
    if (!objectUrl) return;
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 360,
        aspectRatio: '4 / 3',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {urlQuery.isPending ? (
        <CircularProgress size={22} />
      ) : urlQuery.isError ? (
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Icon name="error" />
          <Text variant="label-small" color="text.secondary">
            {t('createListing.photo.thumbError', 'Could not load the photo.')}
          </Text>
        </Box>
      ) : objectUrl ? (
        <Box
          component="img"
          src={objectUrl}
          alt={t('createListing.photo.thumbAlt', 'Harvest cover photo')}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : null}
    </Box>
  );
}

// --------------------------------------------------------------------------
// Step 4 — Review & publish
// --------------------------------------------------------------------------

interface ReviewStepProps {
  harvest: HarvestDto;
  priceMicro: string | null;
  grade: Grade | '';
  note: string;
  onBack: () => void;
  onPublish: () => void;
  isPublishing: boolean;
  publishError: unknown;
  t: TFunction;
}

function ReviewStep({
  harvest,
  priceMicro,
  grade,
  note,
  onBack,
  onPublish,
  isPublishing,
  publishError,
  t,
}: ReviewStepProps) {
  const askPerKg = priceMicro ? microToZarp(priceMicro) : null;

  return (
    <Stack spacing={2}>
      <Box>
        <Text variant="title-large" as="h3">
          {t('createListing.review.title', 'Review & publish')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t(
              'createListing.review.subtitle',
              'Check the details, then publish so verified buyers can make offers.',
            )}
          </Text>
        </Box>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <ReviewRow
              label={t('createListing.review.harvest', 'Harvest')}
              value={`${shortId(harvest.id)} · ${groupDigits(harvest.totalKg)} kg${
                harvest.crop ? ` · ${harvest.crop}` : ''
              }`}
            />
            <Divider />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Text variant="body-medium" color="text.secondary">
                {t('createListing.review.askPrice', 'Asking price')}
              </Text>
              {askPerKg != null ? (
                <Money amount={askPerKg} unit={t('createListing.review.perKg', '/kg')} size="md" />
              ) : (
                <Text variant="body-medium" color="text.primary">
                  —
                </Text>
              )}
            </Box>
            {grade ? (
              <>
                <Divider />
                <ReviewRow
                  label={t('createListing.review.grade', 'Grade')}
                  value={t('createListing.details.gradeOption', {
                    defaultValue: 'Grade {{grade}}',
                    grade,
                  })}
                />
              </>
            ) : null}
            {note.trim() ? (
              <>
                <Divider />
                <ReviewRow
                  label={t('createListing.review.note', 'Note')}
                  value={note.trim()}
                />
              </>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      {publishError ? (
        <Alert severity="error">
          {publishError instanceof FseApiError
            ? publishError.message
            : t('createListing.review.error', 'Could not publish this listing.')}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="text" onClick={onBack} disabled={isPublishing}>
          {t('createListing.back', 'Back')}
        </Button>
        <Button
          variant="contained"
          onClick={onPublish}
          disabled={isPublishing || priceMicro == null}
          startIcon={isPublishing ? <CircularProgress size={16} /> : undefined}
        >
          {isPublishing
            ? t('createListing.review.publishing', 'Publishing…')
            : t('createListing.review.publish', 'Publish listing')}
        </Button>
      </Box>
    </Stack>
  );
}

/** Label/value row for the review summary. */
function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      <Text variant="body-medium" color="text.secondary">
        {label}
      </Text>
      <Text variant="body-medium" color="text.primary" align="right">
        {value}
      </Text>
    </Box>
  );
}
